import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { signInWithGithub } from '../globals/firebase';
import { ShieldCheckIcon } from 'lucide-preact';
import { Logo } from '@/components/Logo';

// Declare the launcher auth API that may be exposed by the preload script
declare global {
  interface Window {
    launcherAuth?: {
      sendIdToken: (idToken: string) => void;
      sendError: (error: string) => void;
      isLauncherLogin: boolean;
    };
  }
}

export function LoginPage() {
  const [, setLocation] = useLocation();
  const [authenticating, setAuthenticating] = useState(false);
  const [countdown, setCountdown] = useState(3);
  // Check for browser-based launcher authentication via query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isBrowserLauncherLogin = urlParams.get('launcher') === 'true';

  // State for CLI success message
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (showSuccess) {
      setTimeout(
        () => setLocation('/'), 3000
      )
      setCountdown(3);
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showSuccess]);


  const handleSignIn = async () => {
    try {
      setAuthenticating(true);
      const result = await signInWithGithub();
      // Check if we're running inside the launcher login window
      if (isBrowserLauncherLogin && result.user) {
        // Browser-based launcher authentication - redirect to nitros:// protocol
        const idToken = await result.user.getIdToken();
        window.location.href = `nitros://auth?idToken=${encodeURIComponent(idToken)}`;
        // Update UI to show success
        setShowSuccess(true);
      }
    } catch (error) {
      console.error('Sign in failed:', error);
    } finally {
      setAuthenticating(false);
    }
  };

  return (
    <div class="absolute inset-0 bg-black/10 backdrop-blur-[160px] z-20 w-full h-full flex-1 col aic jcc"
      onClick={() => {
        if (!authenticating) {
          handleSignIn();
        }
      }}
    >
      {showSuccess ? (
        <div class="col aic jcc gap-4">
          <ShieldCheckIcon class="w-40 h-40 text-white/60" strokeWidth={1.8} />
          <p class="text-(lg neutral-300 center) font-medium max-w-[32ch] leading-relaxed">
            Authentication was successful, you should now be signed in everywhere
          </p>
          <span class="text-(sm neutral-300/20) text-center">Redirecting you to the homepage in {countdown} seconds</span>
        </div>
      ) : authenticating ? (
        <div class="col aic jcc gap-12">
          <Logo size={56} />
          <div class="col aic jcc gap-2">
            <p class="text-(lg neutral-300 center) font-medium max-w-[32ch] leading-relaxed">
              Please accept the GitHub authentication request in the popup window
            </p>
          </div>
        </div>
      ) : (
        <div class="col aic jcc gap-12">
          <Logo size={56} />
          <p class="text-(lg neutral-300 center) max-w-[25ch] leading-relaxed">
            Click anywhere to sign in to Nitros with your GitHub account
          </p>
        </div>
      )}
    </div>
  );
}
